import { useState, useEffect } from 'react';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gift, Loader2, Star, Search } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('id-ID');

export default function StoreRewards() {
  const subdomain = useStoreSubdomain();
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(null);

  const fetchInfo = async () => {
    if (!email.trim()) { toast.error('Masukkan email Anda'); return; }
    setLoading(true);
    try {
      const r = await api.get(`/store/${subdomain}/loyalty-info?email=${encodeURIComponent(email.trim())}`);
      setData(r.data);
      if (!r.data.enabled) toast.info('Program loyalty belum aktif di toko ini');
    } catch {
      toast.error('Gagal memuat data loyalty');
    } finally { setLoading(false); }
  };

  const claimReward = async (rewardId) => {
    setClaiming(rewardId);
    try {
      const r = await api.post(`/store/${subdomain}/loyalty-claim-reward`, {
        customer_email: email.trim(),
        reward_id: rewardId,
      });
      toast.success(r.data.message);
      // Re-fetch to update balance
      fetchInfo();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal klaim reward');
    } finally { setClaiming(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
            <Gift size={32} className="text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Loyalty Rewards</h1>
          <p className="text-gray-500 text-sm mt-1">Tukar poin Anda dengan hadiah menarik</p>
        </div>

        {/* Email lookup */}
        <div className="flex gap-2 mb-8">
          <Input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Masukkan email Anda"
            className="h-12"
            onKeyDown={e => e.key === 'Enter' && fetchInfo()}
          />
          <Button onClick={fetchInfo} disabled={loading} className="h-12 px-6 bg-amber-500 hover:bg-amber-600">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </Button>
        </div>

        {data && !data.enabled && (
          <div className="text-center text-gray-400 py-12">
            <p>Program loyalty belum aktif di toko ini.</p>
          </div>
        )}

        {data && data.enabled && (
          <>
            {/* Points balance */}
            <div className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl p-6 text-white mb-8 shadow-lg">
              <p className="text-amber-100 text-sm">Saldo Poin Anda</p>
              <p className="text-4xl font-extrabold mt-1">{fmt(data.points)} <span className="text-lg font-normal">poin</span></p>
              <p className="text-amber-100 text-xs mt-2">
                1 poin = Rp {fmt(data.settings?.redemption_value || 100)} • Earn 1 poin setiap Rp {fmt(data.settings?.points_per_rupiah || 1000)} belanja
              </p>
            </div>

            {/* Rewards catalog */}
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Star size={20} className="text-amber-500" /> Katalog Reward
            </h2>

            {data.rewards?.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <Gift size={48} className="mx-auto mb-3 opacity-30" />
                <p>Belum ada reward tersedia saat ini.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.rewards.map(r => {
                  const canClaim = data.points >= r.points_required;
                  return (
                    <div key={r.id} className="flex gap-4 bg-white rounded-xl p-4 border shadow-sm">
                      {r.image_url ? (
                        <img src={r.image_url} alt={r.name} className="w-20 h-20 rounded-lg object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shrink-0">
                          <Gift size={28} className="text-amber-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{r.name}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{r.description || '-'}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm font-bold text-amber-600">{fmt(r.points_required)} poin</span>
                          {r.stock > 0 && <span className="text-xs text-gray-400">• Stok: {r.stock}</span>}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Button
                          onClick={() => claimReward(r.id)}
                          disabled={!canClaim || claiming === r.id}
                          className={canClaim ? 'bg-amber-500 hover:bg-amber-600' : ''}
                          size="sm"
                        >
                          {claiming === r.id ? <Loader2 size={14} className="animate-spin" /> : 'Klaim'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
